import { useMemo } from 'react';
import { useAdvancedSearchRequestBuilder } from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { useAppSelector } from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';

export const REVIEWS_PAGE_SIZE = 108; // Multiple of 12 since we show 12 cards per page
export const useReviewTrendsReviewGridData = (pageNumber: number, starFilter = ['1', '2', '3', '4', '5']) => {
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const categories = useAppSelector((state) => state.categories);
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const { startWeek, endWeek } = mainTimePeriod;
  const categoryIds = useMemo(() => categories.map((category) => Number(category.id)), [categories]);

  const reviewDocumentsRequestId = 'reviewsGridDocuments-product--1';

  const reviewDocumentsRequestBuilder = useAdvancedSearchRequestBuilder(reviewDocumentsRequestId, 'beacon-reviews');

  const reviewDocumentsRequest = useMemo(() => {
    reviewDocumentsRequestBuilder
      .setPageNumber(pageNumber)
      // set page size can be divide by 3 (3 cards in a row)
      .setPageSize(REVIEWS_PAGE_SIZE)
      .setPeriod('year')
      .setDoAggregation(false)
      .setReturnDocuments(true)
      .addConditionTermFilter('categoryId', 'should', categoryIds)
      .addConditionTermFilter('reviewText', 'match', [''])
      .addConditionTermFilter('stars', 'match', starFilter)
      .addConditionRangeFilter('weekId', startWeek, endWeek)
      .setSearchBy('child')
      .addSortFieldWithOnlyFieldName('timestamp', 'desc')
      .addSortFieldWithOnlyFieldName('stars', 'desc')
      // Scope to the current entity the same way the donut chart does, so the review cards and the
      // review counts are built from the same set of reviews. This also covers segments and
      // business units, which the previous per-entity-type branches did not.
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters);

    return reviewDocumentsRequestBuilder.build();
  }, [
    applyEntityFilters,
    categoryIds,
    endWeek,
    removeRetailPriceRangeFilters,
    starFilter,
    pageNumber,
    reviewDocumentsRequestBuilder,
    startWeek
  ]);

  const { data, isLoading } = useGenericAdvancedSearch({
    requestId: reviewDocumentsRequestId,
    queryKeys: [reviewDocumentsRequest],
    requestBody: [reviewDocumentsRequest]
  });

  const reviewDocumentData = useMemo(() => {
    const documents = data ? data.data[0].documents : [];
    // The API attaches `product` by looking the review's SKU up in the client's Beacon catalog and
    // silently omits it when the SKU is not in the catalog (e.g. reviews syndicated across product
    // variants that were never onboarded). Those reviews cannot be rendered as a product card.
    return documents.filter((document) => document.product);
  }, [data]);

  return { reviewDocumentData, isLoading };
};
